/**
 * ------------------------------------------------------------
 * Developer Name : Praveen Tripathi
 * Purpose        : Landing MBA hero banner with lead form
 * Location       : features/landing-program/view (MVC presentational)
 * Prefer entry:   ./LandingHeroBanner
 * ------------------------------------------------------------
 */

"use client";

import { usePathname } from "next/navigation";
import styles from "./BannerWithImageNewLandingMBA.module.scss";
import cn from "clsx";
import Markdown from "@/components/atoms/Markdown";
import CommonButton from "@/components/common/Analytics/CustomButton";
import { GAEvent, GA_EVENTS, GA_EVENTS_PAYLOAD, IGAEvent, ga_events_mapping } from "@/helpers/gaEvents";
import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import { StorageService } from "@/helpers/storage.service";
import { ModalTypes } from "@/types/modal";
import {OTPContext, OTPContextProvider,OTPContextWrapper} from "@/components/sections/CommonModals/context";
import ImageTag from "./TagView/ImageTag";
import {  updateStateSession } from "@/context/utils";
import { pushToDataLayer } from "@/helpers/gtag";
import { imgPathReplace } from "@/helpers/imgPathReplace";
import { FREEZE_RFIS } from "@/components/organisms/HeaderOpt/utils";
import useOnScreen from "@/helpers/Onscreen";

const LandingConnectModalFormMBA = dynamic(
  () =>
    import(
      "@/LpRevamp/LandingPageConnectModalMBA/LandingConnectModalFormMBA"
    ),
);


interface BannerWithImageProps {
  BannerData: any;
  isMobile: boolean;
  specializationText: string;
  Custom10PgType?: string;
  associatedPrograms: Array<ProgramDetails>;
  ProgramId: string;
  NewBannerUI: NewBannerUI;
  LandingPageBrochure?: any;
  isSpecificPage?: boolean;
  isMbaPage?: boolean;
  isNextGen?: boolean
  SponsoredText?: string
  Webinar?:WebinarUI
  isLambdaApi?: boolean;
  tagText?: string
  slug?: string;
  /** From landing mapper — Offered by Amity University Rajasthan */
  offeredByAmity?: boolean;
  /** From landing mapper — psychology LP background position */
  isPsychologyBanner?: boolean;
  /** Pre-resolved LCP image URL from mapper */
  lcpImageHref?: string;
}

export default function BannerWithImageNewLandingMBA({
  BannerData,
  isMobile,
  specializationText,
  Custom10PgType = "",
  associatedPrograms,
  ProgramId: _ProgramId,
  NewBannerUI,
  LandingPageBrochure,
  isSpecificPage,
  isMbaPage = false,
  isNextGen = false,
  Webinar,
  isLambdaApi,
  SponsoredText = "",
  tagText = "",
  slug = "",
  offeredByAmity = false,
  isPsychologyBanner = false,
  lcpImageHref,
}: BannerWithImageProps) {
  const isWebinar=Webinar?.isEnabled

  const ref = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(ref);
  const pathName = usePathname();

  const [clickedInsideLeadForm, setClickedInsideLeadForm] = useState(false);

  const {
    PROGRAM_TAB_DOWNLOAD_BROCHURE,
    PROGRAM_CARD_DOWNLOAD_BROCHURE,
    PROGRAM_DOWNLOAD_BROCHURE,
    LEAD_INITIATE
  } = GAEvent;
  const [gaData, setGaData] = useState<IGAEvent | null>(null);

  function getGaSessionData(): {
    isMbaPage?: boolean;
    isNavbarClick?: boolean;
  } | null {
    return StorageService.get("programDetailsGAInfo") ?? {};
  }

  useEffect(() => {
    updateStateSession({
      formSectionName: "Download Brochure",
      leadStartingCta: BannerData?.CTA?.text || "NA"
    });
  }, []);

  useEffect(() => {
    const enrollNowRef = document.getElementById("enroll-now-cta");

    if (enrollNowRef) {
      if (onScreen) {
        enrollNowRef.style.display = "none";
      } else {
        enrollNowRef.style.display = "block";
      }
    }
  }, [onScreen]);


  useEffect(() => {
    try {
      const { isMbaPage = false, isNavbarClick } = getGaSessionData();
      if (ga_events_mapping?.[pathName] || isMbaPage) {
        let data: any;
        if (isNavbarClick !== undefined) {
          data = {
            ...GA_EVENTS_PAYLOAD.get(
              isNavbarClick ? PROGRAM_TAB_DOWNLOAD_BROCHURE : PROGRAM_CARD_DOWNLOAD_BROCHURE
            ),
            pageType: isNavbarClick ? "Program Nav" : "Program Card",
          };
        } else {
          data = {
            ...GA_EVENTS_PAYLOAD.get(PROGRAM_DOWNLOAD_BROCHURE),
            pageType: "Program Details",
          };
        }
        setGaData(data);
      }
    } catch (error) {
      console.error("Error parsing programDetailsGAInfo from sessionStorage", error);
    }
  }, []);


  const handleClickInsideLeadForm = (e) => {
    if (!clickedInsideLeadForm) {
      StorageService.set("timeConnectModal", true)
      
      pushToDataLayer(
        {
          event :	"lead_initiate",
          cta_text : "NA",
          section_name : "hero_banner",
          header_title :	isWebinar?webinarText:BannerData?.Heading?.text || "NA",
          page_type	: "Campaign Page",
          page_url : window.location.href || "NA"
        }
      )
      updateStateSession(
        {
          parentSection : "hero_banner"
        }
      )
      setClickedInsideLeadForm(true);
    }
  };

  const handleClickOutsideLeadForm = (e) => {
    const formElement = document.getElementById("new-lead-form");
    if (formElement && !formElement.contains(e.target)) {
      setClickedInsideLeadForm(false); // Reset if clicked outside
    }
  };
  useEffect(()=>{
    const stickyElement = document.getElementById('botton-nav-container');

    if(stickyElement){
      if(onScreen){
        stickyElement.style.display = 'none'
      }else{
        stickyElement.style.display = 'flex'
      }  
    }
   
  },[onScreen])

  // Add event listener for clicks outside the open lead form
  useEffect(() => {
    document.addEventListener("click", handleClickOutsideLeadForm);
    return () => {
      document.removeEventListener("click", handleClickOutsideLeadForm);
    };
  }, []);

  const newurl = imgPathReplace(
    lcpImageHref || NewBannerUI?.bannerImage?.data?.attributes.url,
  );
  const sponsorSubtitle = "Post successful completion, apply for paid internship *";
  const formDisable=Webinar?.isWebinarEnd
  const webinarText= Webinar?.Description?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ').trim(); 
  const programDetails = associatedPrograms[0]?.attributes?.BasicDetails
  const brochure =  LandingPageBrochure?.data?.attributes?.url
  const usePsychologyBg = isPsychologyBanner || pathName?.includes("ma-in-psychology");
  const bannerBackgroundStyle = newurl
    ? {
        background: `url(${imgPathReplace(newurl)}) no-repeat`,
        backgroundPosition: usePsychologyBg && isMobile ? "85% center" : "50% center",
        backgroundSize: "cover" as const,
      }
    : undefined;

  return (
    <div  className="container">
      <div
        className={cn(isSpecificPage ? styles.BannerImage__containerSpecific : styles.BannerImage__container, styles.BannerImage__container, usePsychologyBg && styles.BannerImage__containerPsychology)}
        style={!isMobile ? bannerBackgroundStyle : undefined}
      >
        {/* Left section with box shadow for blue */}
        <div className={cn(isSpecificPage ? styles.BannerImage__leftContainerSpecific : '', styles.BannerImage__leftContainer, styles.BannerImage__leftContainer, "text-primary-blue")}
          style={isMobile ? bannerBackgroundStyle : undefined}
        >
          {/* Upper subtitle and title section */}
          {/* Subtitle */}
          <div className={styles.mainContent} style={!isMobile && NewBannerUI?.tagTextHTML ? { minHeight: 0 } : {}}>
            {BannerData?.ProgramType?.enabled ? (
              <p style={{ color: 'white' }} className={cn(styles.BannerImage__subtitle, "uppercase")}>
                {BannerData?.ProgramType?.text}
              </p>
            ) : null}
            {/* Title */}
            {BannerData?.Heading?.enabled ? (
              <Markdown as="h1" id="programpage-heading" className={cn(styles.BannerImage__title)}>
                {BannerData?.Heading?.text.replace(/^#\s+(.*)$/gm, "$1")}
              </Markdown>
            ) : null}
            {isNextGen && BannerData?.Sponsor?.enabled && (
              <div className={cn(styles.sponsor)}>
                <div>
                  <h6>{BannerData?.Sponsor?.Title}</h6>
                </div>
                <div>
                  <img
                    src={BannerData?.Sponsor?.Image?.data?.attributes?.url}
                    alt={BannerData?.Sponsor?.Image?.data?.attributes?.alternativeText}
                    loading="lazy"
                  />
                </div>
              </div>
            )}
            {SponsoredText?.length > 0 &&
              <div>
                <p className={styles.tagTextWrapper}>
                  {SponsoredText}</p>
              </div>}

            {BannerData?.Duration?.enabled && (
              <div className={styles.durationWrapper}>
                <p className={cn(styles.BannerImage__duration, "uppercase")}>
                  <span className={cn(styles.BannerImage__durationLabel)}>Duration:</span>{" "}
                  {BannerData?.Duration?.text}
                </p>
              </div>
            )}

            {/* CTA */}
            {BannerData?.CTA?.enabled && !isMobile && !FREEZE_RFIS.includes(pathName) && (
              <>
                <div className={cn(styles.BannerImage__cta)}>
                  <div className='flex gap-2'>
                    <CommonButton
                      id="download-brochure-btn"
                      onClickStorePayload={
                        {
                          sectionTitle: "Hero Banner",
                          parentSection: "hero_banner"
                        }
                      }
                      GAData={gaData}
                      GADataNew={{
                        ...GA_EVENTS.get(LEAD_INITIATE),
                        cta_text: BannerData?.CTA?.text,
                        section_name: "hero_banner",
                        header_title: BannerData?.Heading?.text || "NA",
                        isPageType: true,
                        page_url: true
                      }}
                      isEventOnly={true}
                      CTATarget="_self"
                      programTitle={BannerData?.Heading?.text}
                      type={"button"}
                      modalType={ModalTypes.DownloadBrochureLP}
                      scroll={false}
                      color="#ffc907"
                      icon="/_a/icons/left-arrow-blue-down.svg"
                      hoverIcon="/_a/icons/left-arrow-blue-down.svg"
                      modalData={{ specializationText, Custom10PgType,programDetails,brochure }}
                      className={styles.BannerImage__CTAStyle}
                      webEngageEvent={{ eventName: "Download Brochure Clicked" }}
                    >
                      {BannerData?.CTA?.text}
                    </CommonButton>
                  </div>
                </div>
              </>
            )}
            {tagText && <p className="text-[10px] text-white mt-2">{tagText}</p>}
            {BannerData?.CTA?.enabled && isMobile && !FREEZE_RFIS.includes(pathName) && (
              <>
                <div className={cn(styles.BannerImage__cta)}>
                  <div className='flex gap-2'>
                    <CommonButton
                      id="download-brochure-btn"
                      GAData={gaData}
                      CTATarget="_self"
                      isEventOnly
                      GADataNew={{
                        ...GA_EVENTS.get(LEAD_INITIATE),
                        cta_text: BannerData?.CTA?.text,
                        section_name: "hero_banner",
                        header_title: BannerData?.Heading?.text || "NA",
                        isPageType: true,
                        page_url: true
                      }}
                      onClickStorePayload={
                        {
                          parentSection: "hero_banner"
                        }
                      }
                      webEngageEvent={{ eventName: "Download Brochure Clicked" }}
                      programTitle={BannerData?.Heading?.text}
                      type={"button"}
                      modalType={ModalTypes.DownloadBrochureLP}
                      scroll={false}
                      color="#ffcc00"
                      icon="/_a/icons/left-arrow-blue-down.svg"
                      hoverIcon="/_a/icons/left-arrow-blue-down.svg"
                      modalData={{ specializationText, Custom10PgType,programDetails,brochure }}
                      className={styles.BannerImage__CTAStyle}
                    >
                      {BannerData?.CTA?.text}
                    </CommonButton>
                  </div>
                </div>
              </>
            )}
          </div>
          {offeredByAmity && <div className={styles.offered_by}>Offered by Amity University Rajasthan</div>}
          <ImageTag
            htmlData={NewBannerUI?.tagTextHTML}
            image={NewBannerUI?.tagImage?.data?.attributes?.url}
          />
          {/* Course duration */}
          {!isNextGen && BannerData?.Sponsor?.enabled && (
            <div className={cn(styles.sponsor)}>
              <div>
                <h6>{BannerData?.Sponsor?.Title}</h6>
              </div>
              <div>
                <img
                  src={BannerData?.Sponsor?.Image?.data?.attributes?.url}
                  alt={BannerData?.Sponsor?.Image?.data?.attributes?.alternativeText}
                  loading="lazy"
                />
              </div>
            </div>
          )}
          {isSpecificPage && <div className={styles.sponsor_subtitle}> {sponsorSubtitle} </div>}
        </div>

        {/* Right section with image */}
        <div
          ref={ref}
          id="#leadform"
          className={`${styles.leadFormWrapper} `}
          onClick={handleClickInsideLeadForm}
        >
          {!formDisable && (
            <OTPContextProvider isLambdaAPI={isLambdaApi}>
              <OTPContext.Consumer>
                {({ isHeaderHidden }) => (
                  <>
                    <OTPContextWrapper isLambdaAPI={isLambdaApi}>
                      <LandingConnectModalFormMBA
                        isWebinar={isWebinar}
                        associatedPrograms={associatedPrograms}
                        slug={slug}
                        webinar={Webinar}
                      />
                    </OTPContextWrapper>
                  </>
                )}
              </OTPContext.Consumer>
            </OTPContextProvider>
          )}
        </div>
      </div>
      <div className={styles.imgWrapper} style={{ position: "relative" }}>
      </div>
    </div>
  );
}
